/**
 * Designs tier (Phase 65 / BLU-594 PR3b — wired to the apply engine).
 *
 * The set cards are the canonical "what's assigned" view across all three add
 * modes: Drop files (presigned GCS upload — real bytes land now), Google Drive
 * (design-id → live candidate resolve → assign to a slot), and Paste URL
 * (server-validated preview → assign). Empty slots take a dropzone in Drop
 * mode; Drive/URL modes surface an add banner above the cards. Ready sources
 * (uploaded / drive / url) feed the sync scope; a not-yet-uploaded slot stays
 * "not ready" and holds the product back from the run. The print sets control
 * (below) drives which sets are required.
 *
 * Phase 86 (D-09, D-10, D-12, D-13, D-21): a product whose type has Platen
 * Size rules gets, in set mode, one group of the SAME set cards per platen
 * variant (86-12's one grouping), ordered by its smallest size. Every slot in
 * a group is keyed by that platen variant (`platenSlotKey`), takes PNG only,
 * runs the pixel-size check before uploading and mints with the group's key,
 * so each platen variant gets its own stored file. The tier writes the
 * product's `PlatenLayout` snapshot whenever its groups change, so the page
 * can build the run scope for a selected product that is collapsed or on
 * another page. The missing-size prompt (S3) sits above the groups and the
 * Advanced per-size section (S6) at the bottom. A product without rules
 * renders exactly as before.
 *
 * A custom product type is never ruled in SET mode (the sync run treats its
 * files as per-order), so set mode shows it no platen chrome at all. Colour
 * mode publishes straight to Shopify, where the upload resolves every type's
 * platen variant, so colour mode keeps the split and its chrome for it too.
 *
 * Phase 88 (D-04, D-12, S17, S20): a store SKU product (it carries a
 * `storeSku` summary, set only in store SKU mode) takes platen uploads only.
 * The set and colour toggle is hidden and the saved attach mode ignored: a
 * ruled one renders the colour grid's platen rows for its set-up store SKUs
 * (a changed one disabled), an unruled one says it needs a platen size rule,
 * one with no store SKU set up yet says only that, and one with some set up
 * and more still to set up renders the grid for the set-up ones under a line
 * naming how many are left (as Product updates lists it). The first
 * upload into a store SKU that prints from an existing file today warns once
 * per product, inside the pixel check's one dialog. The server refuses any
 * other upload for a store SKU, so this is the honest UI for that rule.
 *
 * 88a.1 (D4, set up on upload): while the server opts in, a not-set-up
 * variant an upload can set up carries `settable` and is an upload target
 * (`productArtworkTarget`). An admin then gets platen cells on a product with
 * nothing set up yet, under one line saying an upload sets it up, and the
 * partly set-up line says the rest can be set up by uploading here too. The
 * "Set up this product's store SKUs first" lock stays only where set up on
 * upload cannot act: a viewer, or no variant carrying `settable` (which is
 * also what an unset server flag looks like). A locked product reads nothing.
 */
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ExternalLink, FileImage, Loader2, RefreshCw, Search, Upload, X } from 'lucide-react';
import {
  isCustomSyncProduct,
  nameListIncludes,
  formatScalePercent,
  BYO_LETTER_DESIGN_SET,
  DESIGN_SET_ORDER,
  type DerivedDesignSet,
  isPlatenSideCovered,
  type DtgPlatenCode,
  type PlatenVariant,
} from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { formatPlatenSizeRange, platenGroupLabel, platenVariantLabel } from '@/lib/platen-size-range';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DesignThumb } from './DesignThumb';
import { PrintSetsControl } from './PrintSetsControl';
import { ColourArtworkGrid, type PlatenReplaceCell, type PlatenReplaceNotice } from './ColourArtworkGrid';
import { LockedHint } from './LockedHint';
import { UploadHint } from './UploadHint';
import { ArtworkTargetPicker } from './ArtworkTargetPicker';
import { MissingSizePrompt } from './MissingSizePrompt';
import { PlatenAdvancedSection } from './PlatenAdvancedSection';
import type { ArtworkUploadTarget } from '../hooks/useArtworkUploadLocation';
import {
  useOrgPlatenContext,
  usePlatenFileState,
  usePlatenFileStateRefresh,
  usePlatenSizeContext,
} from '../hooks/usePlatenSizeContext';
import type { ColourUploadPlatenSkip } from '../hooks/useColourArtworkUpload';
import { PLATEN_SETTINGS_HREF, PLATEN_VARIANTS_HREF, useCanOpenPlatenSettings } from '../hooks/usePlatenSettingsReach';
import { expectedPlatenFilePixels, usePlatenPixelCheck, type PlatenPixelGuard } from '../hooks/usePlatenPixelCheck';
import { SmallFromLargeLine, SmallMadePanel } from './SmallFromLargeLine';
import {
  isProductPlatenRuled,
  isSideMadeFromLarge,
  platenGroupsForProduct,
  platenProductTypeCode,
  platenSizesWithoutFile,
  productArtworkTarget,
  type PlatenGroup,
} from '../lib/platen';
import { acceptReplaceNotice, isReplaceNoticeAccepted, skusPrintingExistingFile } from '../lib/replace-notice';
import { useAttachModePreference, type AttachMode } from '../hooks/useAttachModePreference';
import {
  platenSlotKey,
  slotKey,
  smallByHandColourKey,
  type DesignSet,
  type DesignSide,
  type PlatenLayoutGroup,
  type SlotAssignment,
  type SlotKey,
  type StagedDesignsApi,
} from '../hooks/useStagedDesigns';
import { useDesignUpload } from '../hooks/useDesignUpload';
import { buildPlatenLayout, requiredSets } from '../lib/scope';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';
import type { PreviewProduct } from '../lib/types';

type DesignMode = 'drop' | 'drive' | 'url';
/**
 * Two ways to attach designs to this product:
 *  - `set`   — the canonical light/dark/single set cards; designs are STAGED
 *              locally and applied together on the next sync run.
 *  - `colour`— per-colour PNGs published STRAIGHT to each variant's Shopify
 *              artwork metafield (BLU-655 B2).
 *
 * The choice is a persisted page-level preference, not per-row session state —
 * see `useAttachModePreference`.
 */
type TierMode = AttachMode;

interface DesignsTierProps {
  product: PreviewProduct;
  colours: string[];
  buffer: ProductSyncSettingsBuffer;
  staging: StagedDesignsApi;
  admin: boolean;
  /**
   * The resolved colour-mode upload target plus the candidate list the picker
   * renders. One object rather than two flat props: the target and the reason
   * it is (or is not) resolved have to travel together, or a consumer ends up
   * treating "still loading" and "you have no floors" as the same null.
   */
  artworkTarget: ArtworkUploadTarget;
  /**
   * Phase 92 (D-24): a product on a 3PL's blank groups its colours into the
   * light and dark sets by the client's own per-colour choice (the scan
   * summary's `darkColourNames`), not by the org's dark-colour settings.
   * Absent: today's grouping.
   */
  darkColourNames?: string[];
  /**
   * Phase 86 slice B (86-28, R3-8): a BYO product's light, dark or medium per
   * variant id (the operator's choice, else the metafield's). The set cards
   * follow it, so a Medium variant gets medium slots; the drive search is not
   * offered (its matcher reads light and dark names only). Absent: today's
   * grouping.
   */
  byoVariantSets?: Record<string, DerivedDesignSet>;
  /**
   * Phase 92 (D-09): the product is built on a 3PL's blank, so it asks its
   * platen context with the `supply` hint and reads the 3PL's rules even when
   * the client's own rules leave its type unruled. Absent: today's reads.
   */
  supplyContext?: boolean;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const SET_DOT: Record<DesignSet, string> = { light: '#FFFFFF', dark: '#2A2733', single: '#FFFFFF', medium: '#8E8A99' };
/** Render order: the shared set order. `medium` only ever shows for a BYO product whose variants read Medium. */
const SETS = DESIGN_SET_ORDER;
const SIDES = ['front', 'back'] as const;

/** The platen variant a slot belongs to: its `platenSets` key and its platen (for the pixel check). */
interface SlotPlaten {
  key: string;
  platen: DtgPlatenCode;
  /** The px the file must be: the org's Large numbers, else today's table (the drop hint says the same as the check). */
  filePx?: { width: number; height: number } | null;
}

/**
 * Round 4 (86-32, R4-12): an empty Small slot whose Small this run makes from
 * Large (a preset, not by hand, and a Large slot for the same set on the
 * platen). `largeReady`: that Large slot holds a file for this side (staged
 * or uploading, not failed). `ownFile`: a Small file someone uploaded is
 * already published for one of the slot's SKUs on this side (the run keeps it).
 */
interface SlotMadeFromLarge {
  largeReady: boolean;
  ownFile: boolean;
}

/** A slot holding a file that is not a failed upload (an upload in flight counts, so nothing flashes). */
const holdsFile = (a: SlotAssignment | undefined): boolean => !!a && !(a.kind === 'upload' && a.state === 'error');

export function DesignsTier({
  product,
  colours,
  buffer,
  staging,
  admin,
  artworkTarget,
  darkColourNames,
  byoVariantSets,
  supplyContext = false,
}: DesignsTierProps) {
  const { t } = useTranslation('productSync');
  const [mode, setMode] = useState<DesignMode>('drop');
  // Set vs colour. Set mode is the default so the existing sync-run staging
  // flow is untouched unless the operator opts in; the choice is PERSISTED
  // (see TIER_MODE_STORAGE_KEY) because an operator working a batch in colour
  // mode should not be silently thrown back to set mode by a reload.
  const [savedTierMode, changeTierMode] = useAttachModePreference();
  // A store SKU product takes platen uploads only (S17, S20): the colour
  // grid's platen rows, whatever attach mode the operator saved.
  const storeSkuProduct = product.storeSku != null;
  // Needs set-up: some variants can still be set up. With none set up yet
  // the tier is one line and reads nothing (it has no platen rows to show);
  // with some set up it renders the grid for those, as a changed product
  // does, under a line naming how many still need setting up.
  const storeSkuNeeded = product.storeSku?.status === 'needed';
  const storeSkuNothingSetUp = storeSkuNeeded && (product.storeSku?.setUp ?? 0) === 0;
  // 88a.1 (D4): some variant is a target an upload sets up (the server
  // attaches `settable` only while it opts in), so a drop here can set the
  // product up straight away.
  const storeSkuSettable = useMemo(
    () => storeSkuProduct && product.variants.some((v) => productArtworkTarget(product, v).identity === 'settable'),
    [product, storeSkuProduct],
  );
  // Nothing set up and set up on upload cannot act (a viewer, or nothing
  // settable): one line, no cells, no reads.
  const storeSkuLocked = storeSkuNothingSetUp && (!admin || !storeSkuSettable);
  const tierMode: TierMode = storeSkuProduct ? 'colour' : savedTierMode;
  // 86-23: a BYO product's uploads are staged under its type and design ID.
  const upload = useDesignUpload(staging, product.byoDesign?.ok === true && !storeSkuProduct ? product.productType.name : null);
  const canOpenPlatenSettings = useCanOpenPlatenSettings();

  const productStaging = staging.get(product.id);

  // --- platen size rules (Phase 86, D-09) ---------------------------------
  // The ORG read (started by the page when it mounted, so already cached by
  // the time a row expands, and never refetched by one) says whether this
  // product's type is ruled. Unruled: today's cards render at once and this
  // row makes no platen read at all. Ruled: the row waits on its own product
  // read (rules plus this product's overrides, never truncated). Until the
  // org read answers, or if it failed, the product read runs as before.
  // File state is read only for a ruled product's synced SKUs.
  // A locked store SKU product (nothing set up, and set up on upload cannot
  // act) shows one line and reads nothing for its platen rows (it has none
  // to show).
  // A product on a 3PL's blank (Phase 92, D-09) is never "unruled" by the
  // client's own rules: the 3PL's rules apply, so it always makes its one
  // product read, with the `supply` hint. Every other product: as above.
  const orgPlaten = useOrgPlatenContext({ refetchOnMount: false });
  const unruled =
    !supplyContext && orgPlaten.data !== undefined && !isProductPlatenRuled(product, orgPlaten.data.rules);
  const productPlaten = usePlatenSizeContext(
    product.id,
    !unruled && !storeSkuLocked,
    supplyContext ? { supply: true } : undefined,
  );
  const platenData = unruled ? orgPlaten.data : productPlaten.data;
  const platenLoading = !unruled && !storeSkuLocked && productPlaten.isLoading;
  const platenError = !unruled && !storeSkuLocked && productPlaten.isError;
  const platenGroups = useMemo(() => platenGroupsForProduct(product, platenData), [product, platenData]);
  // Round 4: the rules org's Platen variants, from the same read.
  const platenPixels = platenData?.pixels ?? null;
  const platenSkus = useMemo(() => platenGroups?.groups.flatMap((g) => g.skus) ?? [], [platenGroups]);
  // Round 4: the made flag is asked for only when one of this product's
  // platens has a Made from Large preset; the pixel check and drop hints use
  // the same numbers.
  const anyPreset =
    platenPixels !== null &&
    (platenGroups?.groups.some((g) => platenPixels[g.assignment.platen]?.smallScaleBp != null) ?? false);
  const platenFileState = usePlatenFileState(platenSkus, platenGroups !== null && !storeSkuLocked, {
    includeMadeFromLarge: anyPreset,
  });
  const pixelCheck = usePlatenPixelCheck(platenPixels);
  // BLU-1238 (R3-2): the product keeps printing its current files (an active
  // skip platen mark), from the file-state rows above (no extra request).
  const keepsCurrentFiles = platenFileState.keepsCurrentFiles;
  // R3-14: what the last upload run's commits said about the mark. The server
  // list is the authority for the missing sizes; cleared drops the note.
  const [skipOutcome, setSkipOutcome] = useState<{ missingSizes: string[]; notSetUp: boolean } | null>(null);
  const utils = trpc.useUtils();
  const { invalidateFileState } = usePlatenFileStateRefresh(platenSkus);
  const productIdForSkip = product.id;
  const onPlatenSkip = useCallback(
    (outcomes: ColourUploadPlatenSkip[]) => {
      const mine = outcomes.filter((o) => o.productId === productIdForSkip);
      if (mine.length === 0) return;
      if (mine.some((o) => o.platenSkipCleared)) {
        setSkipOutcome(null);
        void utils.productSync.getPlatenSizeContext.invalidate();
        invalidateFileState();
        return;
      }
      const missing = new Set(mine.flatMap((o) => o.platenSkipMissingSizes ?? []));
      setSkipOutcome({
        missingSizes: [...missing],
        notSetUp: mine.some((o) => o.platenSkipReason === 'notSetUp'),
      });
    },
    [productIdForSkip, utils, invalidateFileState],
  );
  // The pre-commit hint (never a block): a kept product with some sizes on
  // platen files and some not.
  const keptSizes = useMemo(
    () => (keepsCurrentFiles && platenGroups ? platenSizesWithoutFile(platenGroups.groups, platenFileState.bySku) : null),
    [keepsCurrentFiles, platenGroups, platenFileState.bySku],
  );
  const missingSizesWarning = skipOutcome?.notSetUp
    ? []
    : skipOutcome && skipOutcome.missingSizes.length > 0
      ? skipOutcome.missingSizes
      : keptSizes && keptSizes.covered.length > 0
        ? keptSizes.missing
        : [];
  const productTypeLabel = product.productType?.name || product.productType?.code || '';

  // Set mode per platen variant. A custom product is never ruled on the
  // server (its files arrive per order), so its set cards stay today's. The
  // shared rule decides custom: the type kind, and for a Mixed type this
  // product's own switch.
  const isCustomType = isCustomSyncProduct(buffer.settings, product.productType?.name ?? '', product.id);
  // A store SKU product never stages set-mode files, so it has no layout.
  const setModeGroups = platenGroups && !isCustomType && !storeSkuProduct ? platenGroups.groups : null;
  // The platen chrome (rules line, missing-size prompt, Advanced) shows only
  // where this mode actually splits files by platen variant.
  const platenChrome = tierMode === 'colour' ? platenGroups : setModeGroups ? platenGroups : null;

  // D-12 (S20): the first upload into a store SKU cell whose SKUs print from
  // an existing file today (a link, and no exact marker on this platen
  // variant) warns once per product per page load. It names the sizes that
  // file serves and the colour, with the platen row's own size label. Read
  // from the file state the page already holds; no extra request.
  const fileStateBySku = platenFileState.bySku;
  const productId = product.id;
  const replaceNoticeFor = useCallback(
    (cell: PlatenReplaceCell): PlatenReplaceNotice | null => {
      if (isReplaceNoticeAccepted(productId)) return null;
      const sizeOf = new Map((cell.group.items ?? []).map((item) => [item.sku, item.sizeCode]));
      const sizes: string[] = [];
      for (const sku of skusPrintingExistingFile(cell.skus, cell.side, cell.group.assignment, fileStateBySku)) {
        const size = sizeOf.get(sku);
        if (size && !sizes.includes(size)) sizes.push(size);
      }
      if (sizes.length === 0) return null;
      const range = formatPlatenSizeRange(t, sizes);
      return {
        title: t('colourArtwork.replaceWarningTitle'),
        body:
          cell.colour === null
            ? t('colourArtwork.replaceWarningBodyAllGarments', { sizes: range })
            : t('colourArtwork.replaceWarningBody', { sizes: range, colour: cell.colour }),
        accept: () => acceptReplaceNotice(productId),
      };
    },
    [fileStateBySku, productId, t],
  );

  // R4-12: this product's "Upload Small by hand instead" choice, bound to it:
  // platen-wide (By set) and, S6b, per colour (By colour, where the platen-
  // wide one counts for every colour). Rebuilt only when this product's own
  // lists change (stable empty lists otherwise), so another product's choice
  // never re-renders this grid.
  const smallByHandPlatens = staging.smallByHandFor(productId);
  const smallByHandColours = staging.smallByHandColoursFor(productId);
  const setSmallByHand = staging.setSmallByHand;
  const setSmallByHandColour = staging.setSmallByHandColour;
  const smallByHand = useMemo(
    () => ({
      isOn: (platen: DtgPlatenCode) => smallByHandPlatens.includes(platen),
      set: (platen: DtgPlatenCode, on: boolean) => setSmallByHand(productId, platen, on),
      isOnFor: (platen: DtgPlatenCode, rowKey: string) =>
        smallByHandPlatens.includes(platen) || smallByHandColours.includes(smallByHandColourKey(platen, rowKey)),
      setFor: (platen: DtgPlatenCode, rowKey: string, on: boolean, rowKeys: readonly string[]) =>
        setSmallByHandColour(productId, platen, rowKey, on, rowKeys),
    }),
    [smallByHandPlatens, smallByHandColours, setSmallByHand, setSmallByHandColour, productId],
  );

  // Stable between renders, so the colour grid's derived values and every
  // cell's effects only re-run when the groups or the file state change.
  const pixelGuard = pixelCheck.guard;
  const colourPlaten = useMemo(
    () =>
      platenGroups
        ? {
            groups: platenGroups.groups,
            fileState: platenFileState.bySku,
            fileStateLoading: platenFileState.isLoading,
            fileStateError: platenFileState.isError,
            pixelGuard,
            onPlatenSkip,
            keepsCurrentFiles,
            // Round 4: the Made from Large preset (the 3PL's for a supply product).
            pixels: platenPixels,
            smallByHand,
            ...(storeSkuProduct ? { replaceNoticeFor } : {}),
          }
        : null,
    [
      platenGroups,
      platenFileState.bySku,
      platenFileState.isLoading,
      platenFileState.isError,
      pixelGuard,
      onPlatenSkip,
      keepsCurrentFiles,
      platenPixels,
      smallByHand,
      storeSkuProduct,
      replaceNoticeFor,
    ],
  );
  // D-24: a supply product groups by the client's own per-colour choice.
  const darkColourList = darkColourNames ?? buffer.settings.darkColours;
  // Phase 86 slice B: a BYO product's letters, per colour (lower case) and in
  // all. Null for every other product, which keeps today's grouping.
  const byo = useMemo(() => {
    if (!byoVariantSets) return null;
    const byColour = new Map<string, DerivedDesignSet[]>();
    for (const v of product.variants) {
      const letter = byoVariantSets[v.id];
      if (!letter) continue;
      const key = (v.colourName ?? '').toLowerCase();
      const list = byColour.get(key) ?? [];
      if (!list.includes(letter)) list.push(letter);
      byColour.set(key, list);
    }
    // Each SKU's own letter (the variant it belongs to), so a platen group
    // lists every size under the set that size reads.
    const bySku = new Map<string, DerivedDesignSet>();
    for (const v of product.variants) {
      const letter = byoVariantSets[v.id];
      const sku = productArtworkTarget(product, v).sku;
      if (letter && sku) bySku.set(sku, letter);
    }
    return { byColour, bySku, letters: Object.values(byoVariantSets) };
  }, [byoVariantSets, product]);
  const layout = useMemo(
    () =>
      setModeGroups
        ? buildPlatenLayout(
            setModeGroups,
            productTypeLabel,
            colours,
            darkColourList,
            byo ? (sku) => byo.bySku.get(sku) : undefined,
            platenPixels,
          )
        : null,
    [setModeGroups, productTypeLabel, colours, darkColourList, byo, platenPixels],
  );

  // The page builds the run scope and the missing-files list from this
  // snapshot, including for products that are collapsed or on another page.
  // Written only once the config read has answered, so a pending or failed
  // read never erases a good snapshot; an equal snapshot is a no-op.
  const { setPlatenLayout } = staging;
  const platenAnswered = platenData !== undefined;
  useEffect(() => {
    if (!platenAnswered) return;
    setPlatenLayout(product.id, layout);
  }, [platenAnswered, setPlatenLayout, product.id, layout]);

  // Sets to render: required (from colours + dark-set) ∪ any set that already
  // holds an assignment (so an orphaned assignment stays visible + removable).
  const setsToShow = useMemo<DesignSet[]>(() => {
    const req = requiredSets(colours, darkColourList, byo?.letters);
    const withAssignments = new Set<DesignSet>();
    for (const key of Object.keys(productStaging)) {
      // Platen variant slots belong to their group, never to today's cards.
      if (key.includes('|')) continue;
      withAssignments.add(key.split('-')[0] as DesignSet);
    }
    const union = new Set<DesignSet>([...req, ...withAssignments]);
    return SETS.filter((s) => union.has(s));
  }, [colours, darkColourList, productStaging, byo]);

  const darkColours = colours.filter((c) => nameListIncludes(darkColourList, c));
  const lightColours = colours.filter((c) => !nameListIncludes(darkColourList, c));
  const countSkus = (names: string[]): number =>
    product.variants.filter((v) => v.colourName && names.some((n) => n.toLowerCase() === v.colourName!.toLowerCase())).length;
  // BYO: a set's colours and SKUs are the variants that read it.
  const byoColoursFor = (set: DesignSet): string[] =>
    colours.filter((c) => (byo?.byColour.get(c.toLowerCase()) ?? []).some((letter) => BYO_LETTER_DESIGN_SET[letter] === set));
  const byoSkuCount = (set: DesignSet): number =>
    product.variants.filter((v) => {
      const letter = byoVariantSets?.[v.id];
      return letter !== undefined && BYO_LETTER_DESIGN_SET[letter] === set;
    }).length;

  // A colour-less product declares no colour option: one 'single' set only, no
  // light/dark to move, so the card reads "All garments" and print sets hide.
  const colourless = colours.length === 0;

  const coloursForSet = (set: DesignSet): string[] =>
    byo && set !== 'single' ? byoColoursFor(set) : set === 'dark' ? darkColours : set === 'light' ? lightColours : colours;

  const setSummary = (set: DesignSet): string => {
    if (colourless && set === 'single') return t('tier2.allGarmentsSummary', { count: product.variantCount });
    const names = coloursForSet(set);
    const count = set === 'single' ? product.variantCount : byo ? byoSkuCount(set) : countSkus(names);
    return t('tier2.setSummary', { names: names.join(', ') || '-', count });
  };

  // A group's set card counts only that group's SKUs (and names only the
  // colours that have a size in it).
  const groupSetSummary = (group: PlatenLayoutGroup, source: PlatenGroup | undefined, set: DesignSet): string => {
    const count = group.skusBySet[set]?.length ?? 0;
    if (colourless && set === 'single') return t('tier2.allGarmentsSummary', { count });
    // BYO: a colour is named only when one of ITS sizes in this group reads the set.
    const inSet = new Set(group.skusBySet[set] ?? []);
    const names = byo
      ? colours.filter((c) => (source?.skusByColour[c.toLowerCase()] ?? []).some((sku) => inSet.has(sku)))
      : coloursForSet(set).filter((c) => (source?.skusByColour[c.toLowerCase()]?.length ?? 0) > 0);
    return t('tier2.setSummary', { names: names.join(', ') || '-', count });
  };

  const setTitle = (set: DesignSet): string => {
    if (set === 'dark') return t('tier2.dark');
    if (set === 'light') return t('tier2.light');
    if (set === 'medium') return t('tier2.medium');
    return colourless ? t('tier2.allGarments') : t('tier2.single');
  };

  const sideName = (side: DesignSide): string => (side === 'front' ? t('tier2.front') : t('tier2.back'));

  const slotLabel = (set: DesignSet, side: DesignSide): string => {
    const sideLabel = sideName(side);
    if (set === 'single') return sideLabel;
    const setLabel = set === 'dark' ? t('tier2.dark') : set === 'medium' ? t('tier2.medium') : t('tier2.light');
    return `${setLabel} · ${sideLabel}`;
  };

  const groupName = (group: PlatenLayoutGroup): string => platenGroupLabel(t, group);
  const platenSetName = (set: DesignSet): string =>
    set === 'dark'
      ? t('platen.set.dark')
      : set === 'light'
        ? t('platen.set.light')
        : set === 'medium'
          ? t('platen.set.medium')
          : t('platen.set.allGarments');

  /** A group shows the sets its sizes use, plus any set that already holds a file there. */
  const groupSets = (group: PlatenLayoutGroup): DesignSet[] =>
    SETS.filter(
      (set) =>
        (group.skusBySet[set]?.length ?? 0) > 0 ||
        SIDES.some((side) => productStaging[platenSlotKey(group.key, set, side)] !== undefined),
    );

  const slotOptions: SlotOption[] = layout
    ? layout.groups.flatMap((group) =>
        groupSets(group).flatMap((set) =>
          SIDES.map((side) => ({
            set,
            side,
            key: platenSlotKey(group.key, set, side),
            label: t('platen.slotLabel', {
              platen: group.platen,
              variant: platenVariantLabel(t, group.platenVariant),
              set: platenSetName(set),
              side: sideName(side),
            }),
            platenVariant: group.platenVariant,
            platen: group.platen,
          })),
        ),
      )
    : setsToShow.flatMap((set) =>
        SIDES.map((side) => ({ set, side, key: slotKey(set, side), label: slotLabel(set, side) })),
      );

  const renderSetCards = (
    sets: DesignSet[],
    options: {
      summary: (set: DesignSet) => string;
      platen: SlotPlaten | null;
      skuCount?: (set: DesignSet) => number;
      /** Round 4: a Small slot made from Large (null: today's slot). */
      madeFromLarge?: (set: DesignSet, side: DesignSide) => SlotMadeFromLarge | null;
    },
  ) => (
    <div className={cn('grid gap-3', sets.length > 1 ? 'md:grid-cols-2' : 'grid-cols-1')}>
      {sets.map((set) => (
        <div key={set} className="overflow-hidden rounded-lg border bg-background">
          <header className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
            <span aria-hidden className="h-3 w-3 rounded-full border border-black/15" style={{ backgroundColor: SET_DOT[set] }} />
            <span className="text-xs font-bold">{setTitle(set)}</span>
            <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{options.summary(set)}</span>
          </header>
          {/* Phase 86 slice B: a BYO set card kept only by its staged files
              (the operator moved every colour off it) says what to do. */}
          {byo && set !== 'single' && options.skuCount?.(set) === 0 && (
            <NoteLine as="p" tone="warning" icon={AlertTriangle} className="px-3 pt-2">
              {t('tier2.setUnused')}
            </NoteLine>
          )}
          {SIDES.map((side) => {
            const key = options.platen ? platenSlotKey(options.platen.key, set, side) : slotKey(set, side);
            return (
              <SlotView
                key={side}
                productId={product.id}
                designCode={product.designCode}
                set={set}
                side={side}
                slot={key}
                platen={options.platen}
                madeFromLarge={options.madeFromLarge?.(set, side) ?? null}
                guard={pixelGuard}
                sideLabel={sideName(side)}
                mode={mode}
                admin={admin}
                assignment={productStaging[key]}
                staging={staging}
                upload={upload.startUpload}
              />
            );
          })}
        </div>
      ))}
    </div>
  );

  const advancedSizes = platenGroups ? [...platenGroups.groups.flatMap((g) => g.sizes), ...platenGroups.uncovered] : [];

  // Round 4 (86-32, R4-12, MUST 9): By set makes a platen's Small from its
  // Large when the rules org has a preset and the product has both on that
  // platen for some set. ONE line per such platen (the shared piece By colour
  // uses) carries the per-product choice; the plan makes the file at run time.
  const presetFor = (platenCode: DtgPlatenCode): number | null => platenPixels?.[platenCode]?.smallScaleBp ?? null;
  const layoutGroupOn = (platenCode: DtgPlatenCode, variant: PlatenVariant): PlatenLayoutGroup | undefined =>
    layout?.groups.find((g) => g.platen === platenCode && g.platenVariant === variant);
  const setLinePlatens = (layout?.smallPresetPlatens ?? []).filter((platenCode) => {
    const small = layoutGroupOn(platenCode, 'Small');
    const large = layoutGroupOn(platenCode, 'Large');
    return (
      !!small &&
      !!large &&
      presetFor(platenCode) !== null &&
      SETS.some((set) => (small.skusBySet[set]?.length ?? 0) > 0 && (large.skusBySet[set]?.length ?? 0) > 0)
    );
  });
  /** An own Small file is staged on this platen (switching back would leave it unused, so it is removed first). */
  const stagedSmallOn = (platenCode: DtgPlatenCode): boolean =>
    Object.entries(productStaging).some(([key, value]) => key.startsWith(`${platenCode}:Small|`) && value !== undefined);
  const madeFromLargeFor =
    (group: PlatenLayoutGroup) =>
    (set: DesignSet, side: DesignSide): SlotMadeFromLarge | null => {
      if (group.platenVariant !== 'Small' || !setLinePlatens.includes(group.platen)) return null;
      if (smallByHand.isOn(group.platen)) return null;
      const large = layoutGroupOn(group.platen, 'Large');
      if (!large || (large.skusBySet[set]?.length ?? 0) === 0) return null;
      const small = { platen: group.platen, platenVariant: 'Small' as const };
      const ownFile = (group.skusBySet[set] ?? []).some(
        (sku) =>
          isPlatenSideCovered(small, fileStateBySku[sku]?.[side] ?? null) && !isSideMadeFromLarge(fileStateBySku[sku], side),
      );
      return { largeReady: holdsFile(productStaging[platenSlotKey(large.key, set, side)]), ownFile };
    };
  /** S6: a Drive or URL file assigned to a Small slot made from Large turns Small by hand on for its platen. */
  const onSmallAssigned = (platenCode: DtgPlatenCode) => {
    if (setLinePlatens.includes(platenCode) && !smallByHand.isOn(platenCode)) smallByHand.set(platenCode, true);
  };
  /** The platen's one Small from Large line (S8: inside its Small group, under the heading). */
  const smallLineFor = (platenCode: DtgPlatenCode) => {
    const byHand = smallByHand.isOn(platenCode);
    const blocked = byHand && stagedSmallOn(platenCode);
    return (
      <SmallFromLargeLine
        platen={platenCode}
        scalePercent={formatScalePercent(presetFor(platenCode)!)}
        byHand={byHand}
        onByHand={(on: boolean) => smallByHand.set(platenCode, on)}
        {...(blocked ? { blockedReason: t('platen.set.derive.removeToMake', { platen: platenCode }) } : {})}
        admin={admin}
      />
    );
  };

  // Where the rules and each platen's pixels are edited: two new-tab links
  // for an admin who can open Production settings, one line for a 3PL client
  // whose 3PL sets both, nothing for anyone else (they could not edit them).
  const settingsLinkClass =
    'inline-flex items-center gap-1 rounded-sm text-foreground underline underline-offset-4 transition-colors duration-200 hover:text-threadheads-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none';
  const settingsLink = (href: string, label: string) => (
    <Link to={href} target="_blank" rel="noopener noreferrer" className={settingsLinkClass}>
      {label}
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
      <span className="sr-only">{t('common:opensInNewTab')}</span>
    </Link>
  );
  const rulesReach = !canOpenPlatenSettings ? (
    <>
      {' '}
      {t('platen.rulesAndPixelsSetBy3pl')}
    </>
  ) : admin ? (
    <>
      {' '}
      {settingsLink(PLATEN_SETTINGS_HREF, t('platen.rulesLink'))}
      <span aria-hidden="true"> · </span>
      {settingsLink(PLATEN_VARIANTS_HREF, t('platen.variantsLink'))}
    </>
  ) : null;

  // S17: no store SKU set up yet, and set up on upload cannot act here (a
  // viewer, or nothing settable). One line, no staging and no cells: the
  // server would refuse any upload for them.
  if (storeSkuLocked) {
    return (
      <LockedHint>{t(admin ? 'tier2.storeSkuSetUpFirst' : 'tier2.storeSkuSetUpFirstViewer')}</LockedHint>
    );
  }

  // 88a.1 (D4): nothing set up yet, but an admin's upload here sets it up
  // (only reached unlocked). Not a lock: the cells below take files.
  const setUpOnUploadHint = storeSkuNothingSetUp ? <UploadHint>{t('tier2.storeSkuSetUpOnUpload')}</UploadHint> : null;

  // Some set up, some still to set up: the set-up ones get their cells (the
  // grid below). With settable variants they get cells too, and this line
  // says an upload here (or Set up) sets them up; without, they have none
  // until Set up runs. Product updates lists the same product the same way.
  const setUpRestCount = product.storeSku?.settable ?? 0;
  let setUpRestLine: JSX.Element | null = null;
  if (storeSkuNeeded && !storeSkuNothingSetUp && product.storeSku) {
    setUpRestLine = storeSkuSettable ? (
      <UploadHint>
        {t(admin ? 'tier2.storeSkuSetUpRestOnUpload' : 'tier2.storeSkuSetUpRestOnUploadViewer', { count: setUpRestCount })}
      </UploadHint>
    ) : (
      <LockedHint>
        {t(admin ? 'tier2.storeSkuSetUpRest' : 'tier2.storeSkuSetUpRestViewer', { count: setUpRestCount })}
      </LockedHint>
    );
  }

  // A store SKU product whose type has no platen size rule: the server takes
  // its files per platen size only, so there is nothing to upload into yet.
  // (A product with no set-up store SKU at all falls through to the grid,
  // whose colours each say so.)
  const storeSkuNeedsRule =
    storeSkuProduct &&
    platenData !== undefined &&
    !platenLoading &&
    !platenError &&
    platenGroups === null &&
    platenProductTypeCode(product) !== null;
  if (storeSkuNeedsRule) {
    const needsRule = (
      <LockedHint>
        <p className="leading-4">
          {t('tier2.storeSkuNeedsPlatenRule')}
          {rulesReach}
        </p>
      </LockedHint>
    );
    return setUpRestLine ? (
      <div className="space-y-3">
        {setUpRestLine}
        {needsRule}
      </div>
    ) : (
      needsRule
    );
  }

  return (
    <div className="space-y-4">
      {setUpOnUploadHint}
      {setUpRestLine}

      {/* The platen config read failed: we cannot tell which sizes share a
          file, so say so rather than render "no rules". */}
      {platenError && (
        <SectionError
          variant="inline"
          title={t('platen.contextErrorTitle')}
          body={t('platen.contextErrorBody')}
          retryLabel={t('colourArtwork.urlsRetry')}
          retrying={productPlaten.isFetching}
          onRetry={() => void productPlaten.refetch()}
        />
      )}

      {/* Ruled product: one set of files per platen variant (no toggle).
          The rules open in a NEW tab: staged set-mode files live in this
          page's memory and a same-tab navigation would drop them. Only an
          admin can edit them, and an org without a floor of its own cannot
          open Production settings at all: its 3PL sets them. */}
      {platenChrome && (
        <p className="text-[11px] leading-4 text-muted-foreground">
          {t('platen.rulesLine', { productType: productTypeLabel })}
          {rulesReach}
        </p>
      )}

      {/* BLU-1238 (R3-2, R3-14): a kept product prints its current files;
          an upload of every size moves it. Missing sizes are a warning, never
          a block. Not set up yet: it keeps its files until it is. */}
      {platenChrome && keepsCurrentFiles && (
        <p className="text-[11px] leading-4 text-muted-foreground" role="note">
          {t('platen.kept.note')}
        </p>
      )}
      {platenChrome && missingSizesWarning.length > 0 && (
        <p className="flex items-start gap-1.5 text-[11px] leading-4 text-amber-700" role="status">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
          {t('platen.kept.missingSizes', { sizes: missingSizesWarning.join(', ') })}
        </p>
      )}
      {skipOutcome?.notSetUp && (
        <p className="text-[11px] leading-4 text-muted-foreground" role="status">
          {t('platen.kept.notSetUp')}
        </p>
      )}

      {/* S3 (D-10): sizes the rules do not cover get no slot until added.
          Above the platen variant groups. */}
      {platenChrome && platenChrome.uncovered.length > 0 && (
        <MissingSizePrompt
          productTypeName={product.productType?.name}
          productTypeCode={platenChrome.productTypeCode}
          sizes={platenChrome.uncovered}
          rulesForType={platenData?.rules[platenChrome.productTypeCode]}
          admin={admin}
          keepsCurrentFiles={keepsCurrentFiles}
        />
      )}

      {/* Set vs colour — attach-mode toggle. Session-only per row; default set,
          so the sync-run staging flow is byte-identical until the operator opts
          into publishing artwork straight to Shopify variants. A store SKU
          product has no choice to make (platen uploads only). */}
      {!storeSkuProduct && (
        <Tabs value={tierMode} onValueChange={(v) => changeTierMode(v as TierMode)}>
          <TabsList className="h-8" aria-label={t('tier2.modeToggleLabel')}>
            <TabsTrigger value="set" className="h-6 px-2.5 text-xs">{t('tier2.bySet')}</TabsTrigger>
            <TabsTrigger value="colour" className="h-6 px-2.5 text-xs">{t('tier2.byColour')}</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {tierMode === 'colour' ? (
        <div className="space-y-3">
          {/* A store SKU product has no set mode to contrast with, so it says
              only where its artwork publishes. */}
          {storeSkuProduct ? (
            artworkTarget.location ? (
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                {t('tier2.colourModePublishingFor', { location: artworkTarget.location.name })}
              </p>
            ) : null
          ) : (
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {t('tier2.colourModeExplainer')}
              {artworkTarget.location ? (
                <>
                  {' '}
                  {t('tier2.colourModePublishingFor', { location: artworkTarget.location.name })}
                </>
              ) : null}
            </p>
          )}
          <ArtworkTargetPicker target={artworkTarget} />
          {/* Without the platen config a colour upload could fan one file
              across platen variants (D-01), so the grid waits for it. */}
          {!platenError && (
            <ColourArtworkGrid
              product={product}
              locationId={artworkTarget.location?.id ?? null}
              locationIsLoading={artworkTarget.isLoading}
              needsLocationChoice={artworkTarget.needsChoice}
              admin={admin}
              platenLoading={platenLoading}
              platen={colourPlaten}
            />
          )}
        </div>
      ) : (
        <div className="space-y-4">
      {/* Mode control + staged badge */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          {t('tier2.stagedBadge')}
        </span>
        {admin && (
          <Tabs value={mode} onValueChange={(v) => setMode(v as DesignMode)}>
            <TabsList className="h-8" aria-label={t('tier2.modeLabel')}>
              <TabsTrigger value="drop" className="h-6 px-2.5 text-xs">{t('tier2.mode.drop')}</TabsTrigger>
              {/* Phase 86 slice B: a BYO product takes uploads and links only. */}
              {!byo && <TabsTrigger value="drive" className="h-6 px-2.5 text-xs">{t('tier2.mode.drive')}</TabsTrigger>}
              <TabsTrigger value="url" className="h-6 px-2.5 text-xs">{t('tier2.mode.url')}</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Which sizes share a file is unknown until the platen config answers:
          a file staged now could land in the wrong slot (D-01), so the cards
          wait. A failed read shows the error block above instead. */}
      {platenLoading ? (
        <div role="status" aria-busy="true" aria-label={t('platen.set.loading')} className="grid gap-3 md:grid-cols-2">
          <div className="h-40 animate-pulse rounded-lg border bg-muted/40 motion-reduce:animate-none" />
          <div className="h-40 animate-pulse rounded-lg border bg-muted/40 motion-reduce:animate-none" />
        </div>
      ) : platenError ? null : (
        <>
          {/* Drive / URL add banners (above the cards; they assign into slots). */}
          {admin && mode === 'drive' && !byo && (
            <DriveFinder product={product} staging={staging} slotOptions={slotOptions} onSmallAssigned={onSmallAssigned} />
          )}
          {admin && mode === 'url' && (
            <UrlAdder staging={staging} productId={product.id} slotOptions={slotOptions} onSmallAssigned={onSmallAssigned} />
          )}

          {layout ? (
            // One group of the existing set cards per platen variant (D-09,
            // D-21). The group is a heading row over its cards, not a second
            // bordered box around them (one card level, DESIGN.md section 13).
            <div className="space-y-5">
              {layout.groups.map((group) => {
                const name = groupName(group);
                const source = setModeGroups?.find((g) => g.key === group.key);
                const skuCount = SETS.reduce((n, set) => n + (group.skusBySet[set]?.length ?? 0), 0);
                const headingId = `platen-group-${product.id}-${group.key}`;
                return (
                  <section key={group.key} aria-labelledby={headingId} className="space-y-2">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b pb-1.5">
                      <h4 id={headingId} className="text-sm font-semibold">
                        {name}
                      </h4>
                      <span className="text-[11px] leading-4 text-muted-foreground">
                        {t('platen.groupRange', { range: formatPlatenSizeRange(t, group.sizes) })}
                      </span>
                      <span className="ml-auto text-[11px] leading-4 tabular-nums text-muted-foreground">
                        {t('platen.skuCount', { count: skuCount })}
                      </span>
                    </div>
                    {group.platenVariant === 'Small' && setLinePlatens.includes(group.platen) && smallLineFor(group.platen)}
                    {renderSetCards(groupSets(group), {
                      summary: (set) => groupSetSummary(group, source, set),
                      skuCount: (set) => group.skusBySet[set]?.length ?? 0,
                      platen: { key: group.key, platen: group.platen, filePx: expectedPlatenFilePixels(platenPixels, group.platen) },
                      madeFromLarge: madeFromLargeFor(group),
                    })}
                  </section>
                );
              })}
            </div>
          ) : (
            // Set cards — canonical assignment view
            renderSetCards(setsToShow, { summary: setSummary, platen: null, skuCount: byoSkuCount })
          )}
        </>
      )}

      {/* Print sets — hidden for colour-less products (no colours to move),
          and for a product on a 3PL's blank, whose sets follow the client's
          own per-colour choice in its saved blank choice, not these settings (D-24). */}
      {!colourless && !darkColourNames && !byo && <PrintSetsControl colours={colours} buffer={buffer} admin={admin} />}
        </div>
      )}

      {/* S6 (D-12): per-size platen overrides for this product. */}
      {platenChrome && platenData && (
        <PlatenAdvancedSection
          productId={product.id}
          productTypeCode={platenChrome.productTypeCode}
          sizes={advancedSizes}
          rules={platenData.rules}
          overrides={platenData.overrides[product.id]}
          admin={admin}
          keepsCurrentFiles={keepsCurrentFiles}
        />
      )}

      {pixelCheck.dialog}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One slot: assignment state (upload/drive/url) or an empty add affordance.
// ---------------------------------------------------------------------------

interface SlotViewProps {
  productId: string;
  designCode: string;
  set: DesignSet;
  side: DesignSide;
  /** Where this slot's assignment lives (plain or platen variant key). */
  slot: SlotKey;
  /** Set for a platen variant slot: PNG only, pixel check, minted with the group key. */
  platen: SlotPlaten | null;
  /** Round 4: this empty Small slot is made from Large at run time (null: today's slot). */
  madeFromLarge?: SlotMadeFromLarge | null;
  guard: PlatenPixelGuard;
  sideLabel: string;
  mode: DesignMode;
  admin: boolean;
  assignment: SlotAssignment | undefined;
  staging: StagedDesignsApi;
  upload: (
    productId: string,
    set: DesignSet,
    side: DesignSide,
    designCode: string,
    file: File,
    platenVariantKey?: string,
  ) => Promise<void>;
}

const IMAGE_ACCEPT = 'image/png,image/tiff,image/jpeg,.png,.tif,.tiff,.jpg,.jpeg';
const PNG_ACCEPT = 'image/png,.png';

const looksLikePng = (file: File): boolean => file.type === 'image/png' || /\.png$/i.test(file.name);

function SlotView({
  productId,
  designCode,
  set,
  side,
  slot,
  platen,
  madeFromLarge = null,
  guard,
  sideLabel,
  mode,
  admin,
  assignment,
  staging,
  upload,
}: SlotViewProps) {
  const { t } = useTranslation('productSync');
  const [dragOver, setDragOver] = useState(false);
  // A file refused before upload (not a PNG, or its size unreadable) on a
  // platen variant slot. The slot keeps whatever it held before.
  const [refused, setRefused] = useState<string | null>(null);
  const inputId = `design-${productId}-${slot}`;
  const dark = set === 'dark';
  const accept = platen ? PNG_ACCEPT : IMAGE_ACCEPT;
  // The exact pixels a platen slot expects (P3): the same numbers the pixel check uses.
  const platenDims = platen?.filePx ?? null;

  const remove = () => staging.setAssignment(productId, slot, null);
  // S2: `beforeUpload` runs only once the file passed every check, right before it uploads.
  const onFile = async (file: File | null, beforeUpload?: () => void) => {
    if (!file) return;
    setRefused(null);
    if (!platen) {
      void upload(productId, set, side, designCode, file);
      return;
    }
    // D-13: PNG only, and the pixel size is checked before anything uploads.
    if (!looksLikePng(file)) {
      setRefused(t('platen.set.pngOnly'));
      return;
    }
    const answer = await guard(file, platen.platen);
    if (answer === 'unreadable') {
      setRefused(t('platen.unreadablePng'));
      return;
    }
    if (answer === 'cancel') return;
    beforeUpload?.();
    void upload(productId, set, side, designCode, file, platen.key);
  };
  // A retry re-sends a file the operator already accepted: no second check.
  const retry = (file: File) => void upload(productId, set, side, designCode, file, platen?.key);
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // Clear so choosing the same file again (after "I'll fix it") fires again.
    e.target.value = '';
    void onFile(file);
  };

  const refusedLine = refused ? (
    <p role="alert" className="mt-1 flex flex-wrap items-center gap-2 text-[11px] leading-4 text-destructive">
      <span>{refused}</span>
      <button
        type="button"
        onClick={() => setRefused(null)}
        className="rounded font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        {t('variantArtwork.dismiss')}
      </button>
    </p>
  ) : null;

  // Filled slot.
  if (assignment) {
    return (
      <div className="flex items-center gap-3 border-t px-3 py-2.5 first:border-t-0">
        <span className="w-10 shrink-0 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{sideLabel}</span>
        {assignment.kind === 'drive' ? (
          <span className="grid h-16 w-16 shrink-0 place-items-center rounded-md border bg-muted text-muted-foreground">
            <FileImage className="h-6 w-6" aria-hidden="true" />
          </span>
        ) : (
          <DesignThumb
            url={assignment.url}
            filename={assignment.kind === 'upload' ? assignment.file.name : assignment.url}
            dark={dark}
            enlargeLabel={t('tier2.enlarge', { slot: sideLabel })}
            closeLabel={t('tier2.zoom.close')}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-mono text-[11px] font-semibold" title={assignment.kind === 'drive' ? assignment.name : assignment.url}>
            {assignment.kind === 'upload' ? assignment.file.name : assignment.kind === 'drive' ? assignment.name : assignment.url}
          </p>
          <SlotStateLine assignment={assignment} />
          {refusedLine}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {assignment.kind === 'upload' && assignment.state === 'error' && (
            <button
              type="button"
              onClick={() => retry(assignment.file)}
              className="grid h-7 w-7 place-items-center rounded-md text-amber-700 transition-colors duration-200 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
              aria-label={t('upload.retry')}
              title={t('upload.retry')}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
          {admin && mode === 'drop' && assignment.kind === 'upload' && (
            // The file input sits INSIDE the label as sr-only (never
            // display:none), so Tab reaches it and the label shows the ring.
            <label
              className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 motion-reduce:transition-none"
              title={t('tier2.replace', { slot: sideLabel })}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              <input
                id={inputId}
                type="file"
                accept={accept}
                className="sr-only"
                aria-label={t('tier2.replace', { slot: sideLabel })}
                onChange={onInputChange}
              />
            </label>
          )}
          {admin && (
            <button
              type="button"
              onClick={remove}
              className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
              aria-label={t('tier2.remove', { slot: sideLabel })}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Round 4 (86-32): an empty Small slot made from Large at run time. A quiet
  // panel with words only (the choice is on the platen's line above). A file
  // dropped on it is still taken: once it passes the PNG and pixel checks,
  // Small by hand turns on for the product's platen, then the file stages (a
  // staged Small always wins at plan time).
  if (madeFromLarge && platen) {
    const lines = madeFromLarge.ownFile
      ? [t('platen.derive.byHand.ownFile')]
      : [
          t('platen.set.derive.defaultPanel'),
          ...(!madeFromLarge.largeReady ? [t('platen.set.derive.noLargeYet')] : []),
        ];
    const takeByHand = (file: File | null) =>
      void onFile(file, () => staging.setSmallByHand(productId, platen.platen, true));
    return (
      <div className="flex items-start gap-3 border-t px-3 py-2.5 first:border-t-0">
        <span className="mt-2 w-10 shrink-0 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{sideLabel}</span>
        <div className="min-w-0 flex-1">
          <SmallMadePanel
            minH="min-h-[4.5rem]"
            className="py-3"
            lines={lines}
            {...(admin && mode === 'drop' ? { onDropFile: takeByHand } : {})}
          />
          {refusedLine}
        </div>
      </div>
    );
  }

  // Empty slot.
  if (!admin) {
    return (
      <div className="flex items-center gap-3 border-t px-3 py-2.5 first:border-t-0">
        <span className="w-10 shrink-0 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{sideLabel}</span>
        <span className="text-[11px] text-muted-foreground">{t('tier2.emptyReadOnly')}</span>
      </div>
    );
  }

  if (mode !== 'drop') {
    // Drive/URL modes add via their banner; empty slot shows a muted hint.
    return (
      <div className="flex items-center gap-3 border-t px-3 py-2.5 first:border-t-0">
        <span className="w-10 shrink-0 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{sideLabel}</span>
        <span className="text-[11px] text-muted-foreground/70">
          {mode === 'drive' ? t('tier2.emptyDrive') : t('tier2.emptyUrl')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 border-t px-3 py-2.5 first:border-t-0">
      <span className="mt-2 w-10 shrink-0 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{sideLabel}</span>
      <div className="min-w-0 flex-1">
        {/* Keyboard reachable: the input is sr-only INSIDE the label (never
            display:none), and the label shows the focus ring, the same
            pattern as the colour-mode cell. */}
        <label
          onDrop={(e) => { e.preventDefault(); setDragOver(false); void onFile(e.dataTransfer.files?.[0] ?? null); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={cn(
            'flex cursor-pointer flex-col items-center gap-1 rounded-md border-2 border-dashed px-3 py-3 text-center transition-colors duration-200 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 motion-reduce:transition-none',
            dragOver ? 'border-threadheads-purple bg-threadheads-purple/5' : 'border-input hover:border-threadheads-purple/40',
          )}
        >
          <Upload className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-xs font-medium text-foreground">
            {side === 'front' ? t('tier2.dropFront') : t('tier2.dropBack')}
          </span>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {platenDims
              ? t('platen.set.dropHint', { platen: platen?.platen, width: platenDims.width, height: platenDims.height })
              : t('tier2.dropHint')}
            {side === 'back' ? ` ${t('tier2.dropBackHint')}` : ''}
          </span>
          <input id={inputId} type="file" accept={accept} className="sr-only" onChange={onInputChange} />
        </label>
        {refusedLine}
      </div>
    </div>
  );
}

function SlotStateLine({ assignment }: { assignment: SlotAssignment }) {
  const { t } = useTranslation('productSync');
  if (assignment.kind === 'upload') {
    if (assignment.state === 'uploading' || assignment.state === 'staged') {
      return (
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          {t('upload.uploading')}
        </p>
      );
    }
    if (assignment.state === 'error') {
      return <p className="mt-0.5 text-[11px] text-destructive" role="alert">{assignment.error ?? t('upload.failed')}</p>;
    }
    return (
      <p className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="tabular-nums">{formatSize(assignment.file.size)}</span>
        <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
          <CheckCircle2 className="h-2.5 w-2.5" aria-hidden="true" />
          {t('upload.uploaded')}
        </span>
      </p>
    );
  }
  if (assignment.kind === 'drive') {
    return (
      <p className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
        {assignment.size != null && <span className="tabular-nums">{formatSize(assignment.size)}</span>}
        <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold">{t('tier2.fromDrive')}</span>
      </p>
    );
  }
  return (
    <p className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
      {assignment.byteSize != null && <span className="tabular-nums">{formatSize(assignment.byteSize)}</span>}
      <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold">{t('tier2.fromUrl')}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Drive finder — design-id → live candidate resolve → assign to a slot.
// ---------------------------------------------------------------------------

interface SlotOption {
  set: DesignSet;
  side: DesignSide;
  key: SlotKey;
  label: string;
  /** Present on a ruled product's platen variant slots. */
  platenVariant?: PlatenVariant;
  /** Present on a ruled product's platen variant slots: PNG only there (D-13). */
  platen?: DtgPlatenCode;
}

const isPngName = (name: string): boolean => /\.png$/i.test(name.trim());

/** S6: a file assigned to a Small slot (the tier turns Small by hand on when that Small is made from Large). */
type OnSmallAssigned = (platen: DtgPlatenCode) => void;

function DriveFinder({ product, staging, slotOptions, onSmallAssigned }: {
  product: PreviewProduct; staging: StagedDesignsApi; slotOptions: SlotOption[]; onSmallAssigned: OnSmallAssigned;
}) {
  const { t } = useTranslation('productSync');
  const [designId, setDesignId] = useState(product.designCode);
  const resolve = trpc.productSync.resolveDriveDesignCandidates.useMutation();

  const candidates = useMemo(() => {
    const data = resolve.data;
    if (!data || !data.enabled) return [];
    return [...(data.match ? [data.match] : []), ...data.partials];
  }, [resolve.data]);

  return (
    <div className="rounded-lg border bg-background p-4">
      <p className="text-xs font-semibold">{t('tier2.drive.title')}</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={`drive-${product.id}`} className="text-[11px] font-medium text-muted-foreground">{t('tier2.drive.designIdLabel')}</label>
          <Input id={`drive-${product.id}`} value={designId} onChange={(e) => setDesignId(e.target.value)} className="h-8 w-40 font-mono text-xs" />
        </div>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={!designId.trim() || resolve.isPending} onClick={() => resolve.mutate({ designCode: designId.trim() })}>
          {resolve.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Search className="h-3.5 w-3.5" aria-hidden="true" />}
          {t('tier2.drive.findFiles')}
        </Button>
      </div>

      {resolve.isError && <p className="mt-2 text-xs text-destructive" role="alert">{resolve.error.message}</p>}
      {resolve.data && !resolve.data.enabled && <p className="mt-2 text-xs text-amber-700">{t('tier2.drive.notConfigured')}</p>}
      {resolve.data?.enabled && 'reason' in resolve.data && resolve.data.reason === 'per_product_type_only' && <p className="mt-2 text-xs text-amber-700">{t('tier2.drive.perProductTypeOnly')}</p>}
      {resolve.data?.enabled && !('reason' in resolve.data) && candidates.length === 0 && <p className="mt-2 text-xs text-muted-foreground">{t('tier2.drive.noMatches', { code: designId.trim() })}</p>}

      {candidates.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {candidates.map((c) => (
            <DriveCandidateRow key={c.id} productId={product.id} candidate={c} slotOptions={slotOptions} staging={staging} onSmallAssigned={onSmallAssigned} />
          ))}
        </div>
      )}
    </div>
  );
}

function DriveCandidateRow({ productId, candidate, slotOptions, staging, onSmallAssigned }: {
  productId: string; candidate: { id: string; name: string; size?: number }; slotOptions: SlotOption[]; staging: StagedDesignsApi;
  onSmallAssigned: OnSmallAssigned;
}) {
  const { t } = useTranslation('productSync');
  // Suggest a slot from filename tokens (light/dark/single, front/back).
  const suggested = useMemo(() => {
    const n = candidate.name.toLowerCase();
    const set: DesignSet | null = n.includes('dark') ? 'dark' : n.includes('single') ? 'single' : n.includes('light') ? 'light' : null;
    const side: DesignSide = n.includes('back') ? 'back' : 'front';
    // Platen variant slots: prefer the variant the file name mentions.
    const variant = slotOptions.find((o) => o.platenVariant && n.includes(o.platenVariant.toLowerCase()))?.platenVariant;
    const match =
      slotOptions.find(
        (o) => (set ? o.set === set : true) && o.side === side && (variant ? o.platenVariant === variant : true),
      ) ??
      slotOptions.find((o) => (set ? o.set === set : true) && o.side === side) ??
      slotOptions[0];
    return match?.key ?? '';
  }, [candidate.name, slotOptions]);
  const [slot, setSlot] = useState<string>(suggested);
  const chosen = slotOptions.find((o) => o.key === slot);
  // A platen variant slot takes PNG only (D-13), the same rule drop mode
  // enforces, so a JPEG never reaches the run and fails after approval.
  const refusedForPlaten = chosen?.platen !== undefined && !isPngName(candidate.name);
  const reasonId = `drive-refused-${candidate.id}`;

  const assign = () => {
    if (!chosen || refusedForPlaten) return;
    if (chosen.platen && chosen.platenVariant === 'Small') onSmallAssigned(chosen.platen);
    staging.setAssignment(productId, chosen.key, { kind: 'drive', fileId: candidate.id, name: candidate.name, size: candidate.size });
    toast.success(t('tier2.drive.assigned', { name: candidate.name, slot: chosen.label }));
  };

  return (
    <div className="space-y-1 rounded-md border px-2.5 py-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <FileImage className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-mono text-[11px]" title={candidate.name}>{candidate.name}</span>
        {candidate.size != null && <span className="shrink-0 text-[10.5px] tabular-nums text-muted-foreground">{formatSize(candidate.size)}</span>}
        <Select value={slot} onValueChange={setSlot}>
          {/* Wide enough for "14x16 Large · All garments · Front": the cut-off
              part would be the platen variant, the part that prevents a misroute. */}
          <SelectTrigger className="h-7 w-auto min-w-40 max-w-full text-xs" aria-label={t('tier2.drive.assignTo')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {slotOptions.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          className="h-7 px-2.5 text-[11px]"
          onClick={assign}
          disabled={!slot || refusedForPlaten}
          aria-describedby={refusedForPlaten ? reasonId : undefined}
        >
          {t('tier2.drive.assign')}
        </Button>
      </div>
      {refusedForPlaten && (
        <p id={reasonId} className="text-[11px] leading-4 text-muted-foreground">
          {t('platen.set.pngOnly')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// URL adder — server-validated preview → assign to a slot.
// ---------------------------------------------------------------------------

function UrlAdder({ staging, productId, slotOptions, onSmallAssigned }: {
  staging: StagedDesignsApi; productId: string; slotOptions: SlotOption[]; onSmallAssigned: OnSmallAssigned;
}) {
  const { t } = useTranslation('productSync');
  const [url, setUrl] = useState('');
  const [slot, setSlot] = useState<string>(slotOptions[0]?.key ?? '');
  const preview = trpc.productSync.previewDesignUrl.useMutation();

  const add = async () => {
    const trimmed = url.trim();
    const opt = slotOptions.find((o) => o.key === slot);
    if (!trimmed || !opt) return;
    try {
      const res = await preview.mutateAsync({ url: trimmed });
      if (!res.ok) {
        toast.error(t('tier2.url.previewError'));
        return;
      }
      // A platen variant slot takes PNG only (D-13), checked on what the
      // server actually fetched, before anything is staged.
      if (opt.platen !== undefined && !String(res.contentType ?? '').toLowerCase().startsWith('image/png')) {
        toast.error(t('platen.set.pngOnly'));
        return;
      }
      if (opt.platen && opt.platenVariant === 'Small') onSmallAssigned(opt.platen);
      staging.setAssignment(productId, opt.key, { kind: 'url', url: trimmed, contentType: res.contentType, byteSize: res.byteSize });
      toast.success(t('tier2.url.added', { slot: opt.label }));
      setUrl('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('tier2.url.previewError'));
    }
  };

  return (
    <div className="rounded-lg border bg-background p-4">
      <p className="text-xs font-semibold">{t('tier2.url.title')}</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor={`url-${productId}`} className="text-[11px] font-medium text-muted-foreground">{t('tier2.url.inputLabel')}</label>
          <Input id={`url-${productId}`} type="url" value={url} placeholder={t('tier2.url.placeholder')} onChange={(e) => setUrl(e.target.value)} className="h-8 font-mono text-xs" />
        </div>
        <Select value={slot} onValueChange={setSlot}>
          <SelectTrigger className="h-8 w-auto min-w-40 max-w-full text-xs" aria-label={t('tier2.drive.assignTo')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {slotOptions.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={!url.trim() || !slot || preview.isPending} onClick={add}>
          {preview.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {t('tier2.url.preview')}
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {t('tier2.url.hint')}
        {slotOptions.some((o) => o.platen !== undefined) ? ` ${t('platen.set.pngOnly')}` : ''}
      </p>
    </div>
  );
}
